import { useMemo } from 'react';
import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { API } from 'api';
import { EvacZone, GeoEventEvacZoneStatus } from 'shared/types';
import { useCacheState } from 'state/useCacheState';
import { ENV } from 'shared/ENV';
import { usePrevious } from 'hooks/usePrevious';
import { GeoEventRegion } from 'features/geoEvents/geoEventRegion.types';

type UseRegionEvacZonesProps = {
  regions?: GeoEventRegion[];
  /**
   * Active evac zones (with statuses) for the current incident, used to determine zone lock state.
   * @warning Must be `geoEvent.evacZoneStatuses`, not live form state - zones cycled
   * through "normal" are transiently absent from form state and would falsely lock.
   */
  evacZoneStatuses?: GeoEventEvacZoneStatus[];
  refetchInterval?: number; // how often to refetch data, default is undefined (no refetch)
  onZoneStateChanged?: (zones: EvacZone[]) => void; // callback for when zones state changes
  enabled?: boolean; // whether to enable the query, default is true
};

type UseRegionEvacZones = Omit<UseQueryResult<EvacZone[], Error>, 'data'> & {
  regionEvacZones: EvacZone[];
};

const fetchEvacZonesForRegions = async (
  regionIds: number[],
  cacheBusterTs: number | null,
): Promise<EvacZone[]> => {
  const params = {
    regionIds: regionIds.toString(),
    showPending: true,
    ...(cacheBusterTs ? { ts: cacheBusterTs } : {}),
  };
  const response = await API.get<EvacZone[]>('evac_zones/', { params });
  return response.data;
};

export const hasZoneStateChanged = (
  currentZone: EvacZone,
  previousZone?: EvacZone,
): boolean => {
  if (!previousZone) {
    // New zone with status or pending review
    return Boolean(currentZone.status || currentZone.isPendingReview);
  }

  // Existing zone with status or pending review changed
  return (
    currentZone.status !== previousZone.status ||
    currentZone.isPendingReview !== previousZone.isPendingReview
  );
};

export const haveZonesStateChanged = (
  zones?: EvacZone[],
  previousZones?: EvacZone[],
): boolean => {
  if (!zones || !previousZones) return false;

  const prevZoneMap = new Map(previousZones.map((zone) => [zone.uidV2, zone]));

  // Check if any zones have meaningful state changes
  return zones.some((zone) => {
    const prevZone = prevZoneMap.get(zone.uidV2);
    return hasZoneStateChanged(zone, prevZone);
  });
};

export const haveRegionsChanged = (
  currentIds: number[],
  previousIds: number[] | undefined,
): boolean => {
  if (!previousIds) return false;
  return previousIds.join(',') !== currentIds.join(',');
};

export const useRegionEvacZones = (
  props: UseRegionEvacZonesProps,
): UseRegionEvacZones => {
  const {
    regions,
    evacZoneStatuses,
    refetchInterval,
    onZoneStateChanged,
    enabled: enabledProp = true,
  } = props;
  const { cacheBusterTs } = useCacheState();
  const regionIds = useMemo(
    () => (regions && regions.map((region) => region.id).sort()) || [],
    [regions],
  );
  const enabled = enabledProp && Boolean(regionIds.length);

  const { data, ...results } = useQuery<EvacZone[]>({
    queryKey: ['evacuation-zones', regionIds, cacheBusterTs],
    queryFn: () => fetchEvacZonesForRegions(regionIds, cacheBusterTs),
    enabled,
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 6, // 6 minutes - must be bigger than staleTime
    // In RTL tests, components frequently re-render.
    // Setting `refetchOnMount: 'always'` would require excessive `nock` mocks
    // or disabling missing interceptor errors. Opting against 'always' in tests.
    refetchOnMount: ENV.VITE_ENV === 'test' ? true : 'always', // Overrides stale data behavior to always refetch on mount.
    refetchOnWindowFocus: `always`,
    refetchInterval,
  });

  // calculate region locked status based on the evacZoneStatuses
  const regionEvacZones = useMemo(() => {
    if (!data) return [];
    if (!evacZoneStatuses) {
      return data;
    }
    return data.map(
      (zone) =>
        ({
          ...zone,
          isLocked:
            zone.status &&
            !evacZoneStatuses.some((eZS) => eZS.evacZone.uidV2 === zone.uidV2),
        }) as EvacZone,
    );
  }, [evacZoneStatuses, data]);

  // set previousData to undefined in this case so the hasZoneStatusChanged check does not notice a change
  // this is to prevent onZoneStateChanged from being called when the cache is populated when initially loading the component
  const previousData = usePrevious(
    !results.isFetchedAfterMount ? undefined : data,
  );

  const previousRegionIds = usePrevious(regionIds);

  // Only detect zone state changes when regions haven't changed,
  // to avoid false positives from comparing zones across different region sets
  const hasChanged =
    !haveRegionsChanged(regionIds, previousRegionIds) &&
    haveZonesStateChanged(data, previousData);

  if (hasChanged && onZoneStateChanged) {
    onZoneStateChanged(regionEvacZones);
  }

  return {
    ...results,
    regionEvacZones,
  };
};
